import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  FIRST_MONTH_DISCOUNT_RATE,
  MEMBERSHIP_BLUE_SHADOW,
  MEMBERSHIP_COLORS,
  PROMO_YEARLY_DISCOUNT_RATE_LABEL,
  YEARLY_DISCOUNT_RATE_LABEL,
} from "@/features/pathpick/features/membership/constants";
import { MembershipBillingCycle } from "@/features/pathpick/features/membership/types";

interface Props {
  value: MembershipBillingCycle;
  // 첫 달 할인이 얹히면 연 결제 실질 할인율이 17%에서 21%로 올라간다.
  isFirstSubscription: boolean;
  onChange: (value: MembershipBillingCycle) => void;
}

function PathPickMembershipBillingCycleToggle({
  value,
  isFirstSubscription,
  onChange,
}: Props) {
  const { t } = useI18nHelper();

  // 결제 주기 라벨 + 할인 접미 문구. 접미는 굵기만 다르게 이어 붙인다.
  // 월 결제 접미는 프로모션 대상일 때만 붙고, 연 결제 접미는 항상 붙는다(할인율만 달라진다).
  const options: {
    id: MembershipBillingCycle;
    label: string;
    suffix?: string;
  }[] = [
    {
      id: "monthly",
      label: t("membership:compare.toggle_monthly"),
      suffix: isFirstSubscription
        ? t("membership:compare.toggle_monthly_promo", {
            rate: FIRST_MONTH_DISCOUNT_RATE,
          })
        : undefined,
    },
    {
      id: "yearly",
      label: t("membership:compare.toggle_yearly"),
      suffix: t("membership:compare.toggle_yearly_promo", {
        rate: isFirstSubscription
          ? PROMO_YEARLY_DISCOUNT_RATE_LABEL
          : YEARLY_DISCOUNT_RATE_LABEL,
      }),
    },
  ];

  return (
    // 두 세그먼트의 고유 폭 합이 ~349px인데 flexShrink 0이라 절대 줄지 않아,
    // 375px에서는 이 토글 하나가 페이지 전체에 가로 스크롤을 만든다.
    // 모바일에서는 트랙이 폭을 다 쓰고 세그먼트가 반반으로 나눠 갖는다.
    <Flex
      gap={"4px"}
      padding={"4px"}
      borderRadius={"8px"}
      background={MEMBERSHIP_COLORS.toggleTrackBg}
      flexShrink={0}
      width={{ base: "100%", md: "auto" }}
    >
      {options.map((option) => {
        const isSelected = value === option.id;
        return (
          <Flex
            key={option.id}
            as={"button"}
            alignItems={"center"}
            justifyContent={"center"}
            // 접미("첫 달 50% 할인")까지 한 줄에 두면 세그먼트가 ~170px이라
            // 두 개가 343px에 안 들어간다. 모바일에서는 라벨 위/접미 아래로 쌓는다.
            flexDirection={{ base: "column", md: "row" }}
            flex={{ base: 1, md: "initial" }}
            minWidth={0}
            gap={{ base: "0px", md: "4px" }}
            height={{ base: "auto", md: "40px" }}
            padding={{ base: "6px 8px", md: "0 16px" }}
            borderRadius={"8px"}
            background={
              isSelected ? MEMBERSHIP_COLORS.blue : "transparent"
            }
            boxShadow={isSelected ? MEMBERSHIP_BLUE_SHADOW : undefined}
            cursor={"pointer"}
            onClick={() => onChange(option.id)}
          >
            <Text
              textStyle={"body-md-semi-bold"}
              fontSize={{ base: "14px", md: "16px" }}
              color={
                isSelected ? "#FFFFFF" : MEMBERSHIP_COLORS.textMuted
              }
            >
              {option.label}
            </Text>
            {option.suffix && (
              <Text
                textStyle={"body-sm-bold"}
                color={
                  isSelected
                    ? MEMBERSHIP_COLORS.chipOnBlue
                    : MEMBERSHIP_COLORS.blueDeep
                }
              >
                {option.suffix}
              </Text>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

export default PathPickMembershipBillingCycleToggle;
